import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, Video, Gauge, WifiOff, RefreshCw } from "lucide-react";

function loadJitsiScript(appId) {
  return new Promise((resolve) => {
    if (window.JitsiMeetExternalAPI) return resolve();
    const s = document.createElement("script");
    s.src = `https://8x8.vc/${appId}/external_api.js`;
    s.async = true;
    s.onload = resolve;
    document.body.appendChild(s);
  });
}

export function JitsiRoom({ appointmentId, roomName, displayName, onClose }) {
  const ref = useRef(null);
  const apiRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null);
  const [lowData, setLowData] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [connLost, setConnLost] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const videoMutedRef = useRef(false);
  const modeRef = useRef("video");
  const lowDataRef = useRef(false);
  const manualRef = useRef(false);
  const connRef = useRef(null);
  const evalConnRef = useRef(null);

  const applyLowData = (next) => {
    const a = apiRef.current;
    if (!a) return;
    try {
      a.executeCommand("setVideoQuality", next ? 180 : 360);
      if (next) {
        if (!videoMutedRef.current) a.executeCommand("toggleVideo"); // audio-only
      } else if (videoMutedRef.current && modeRef.current === "video") {
        a.executeCommand("toggleVideo"); // restore camera
      }
    } catch (e) { console.debug("low-data toggle unavailable:", e); }
  };

  const setLowDataMode = (next, { auto = false } = {}) => {
    lowDataRef.current = next;
    setLowData(next);
    applyLowData(next);
    if (auto && next) toast.info("Weak connection detected — switched to low-data mode.");
  };

  const toggleLowData = () => {
    manualRef.current = true; // user override stops auto-fallback
    setLowDataMode(!lowDataRef.current);
  };

  // Reconnect prompt: surface a friendly "connection lost — rejoin" overlay when the
  // device loses network mid-call. Rejoin fully re-initialises the room (see reloadKey below).
  useEffect(() => {
    const goOffline = () => { setOnline(false); if (apiRef.current) setConnLost(true); };
    const goOnline = () => setOnline(true);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  const rejoin = () => {
    setConnLost(false);
    setErr("");
    setLoading(true);
    setReloadKey((k) => k + 1);
  };

  // Presence heartbeat: tells the backend this user is in the room (used for clinician-busy gating).
  useEffect(() => {
    let alive = true;
    const beat = () => api.post(`/appointments/${appointmentId}/presence`).catch(() => {});
    beat();
    const t = setInterval(() => { if (alive) beat(); }, 15000);
    return () => {
      alive = false;
      clearInterval(t);
      api.delete(`/appointments/${appointmentId}/presence`).catch(() => {});
    };
  }, [appointmentId]);

  // Auto-rejoin: once the network is back after a mid-call drop, quietly reconnect on its own
  // after a short grace period so patients don't have to tap. Manual "Rejoin now" stays as fallback.
  useEffect(() => {
    if (connLost && online) {
      const t = setTimeout(() => rejoin(), 1800);
      return () => clearTimeout(t);
    }
  }, [connLost, online]);

  useEffect(() => {
    let active = true;
    setConnLost(false);
    (async () => {
      try {
        const data = (await api.get(`/appointments/${appointmentId}/room`)).data;
        await loadJitsiScript(data.app_id);
        if (!active || !ref.current) return;
        const cfg = {
          video: { startWithAudioMuted: false, startWithVideoMuted: false },
          audio: { startWithAudioMuted: false, startWithVideoMuted: true },
          text: { startWithAudioMuted: true, startWithVideoMuted: true },
        }[data.mode] || {};
        // FR-013: low-resource / low-bandwidth tuning for weak devices & connections.
        const lowResource = {
          prejoinPageEnabled: false,
          disableSimulcast: true,
          channelLastN: 1,                 // only receive the active speaker's video
          enableLayerSuspension: true,
          resolution: 360,
          constraints: { video: { height: { ideal: 360, max: 360, min: 180 } } },
          disableAudioLevels: true,
          startAudioOnly: data.mode === "text",
          p2p: { enabled: true },
          disableDeepLinking: true,
          videoQuality: { maxBitratesVideo: { low: 120000, standard: 300000, high: 600000 } },
        };
        apiRef.current = new window.JitsiMeetExternalAPI(data.domain, {
          roomName: data.room_id,
          jwt: data.jwt,
          parentNode: ref.current,
          width: "100%",
          height: "100%",
          userInfo: { displayName: data.display_name },
          configOverwrite: { ...lowResource, ...cfg },
          interfaceConfigOverwrite: {
            MOBILE_APP_PROMO: false,
            DISABLE_VIDEO_BACKGROUND: true,
            DISABLE_PRESENCE_STATUS: true,
            SHOW_CHROME_EXTENSION_BANNER: false,
            TILE_VIEW_MAX_COLUMNS: 1,
          },
        });
        setMode(data.mode);
        modeRef.current = data.mode;
        // FR-013: auto low-data fallback on weak connections (Network Information API).
        const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        const evalConn = () => {
          if (!conn || manualRef.current || lowDataRef.current || modeRef.current === "text") return;
          const poor = conn.saveData
            || ["slow-2g", "2g"].includes(conn.effectiveType)
            || (typeof conn.downlink === "number" && conn.downlink > 0 && conn.downlink < 0.6);
          if (poor) setLowDataMode(true, { auto: true });
        };
        connRef.current = conn;
        evalConnRef.current = evalConn;
        apiRef.current.addListener("videoConferenceJoined", () => {
          try { apiRef.current.executeCommand("setVideoQuality", 360); } catch (e) { console.debug("setVideoQuality failed:", e); }
          if (data.mode === "text") apiRef.current.executeCommand("toggleChat");
          setTimeout(evalConn, 1500);
        });
        apiRef.current.addListener("videoMuteStatusChanged", (e) => { videoMutedRef.current = !!e.muted; });
        try { conn?.addEventListener?.("change", evalConn); } catch (e) { console.debug("connection change listener unavailable:", e); }
        apiRef.current.addListener("readyToClose", () => onCloseRef.current?.());
        setLoading(false);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not start the consultation.");
        setLoading(false);
      }
    })();
    return () => {
      active = false;
      try { connRef.current?.removeEventListener?.("change", evalConnRef.current); } catch (e) { console.debug("listener cleanup failed:", e); }
      try { apiRef.current?.dispose(); } catch (e) { console.debug("jitsi dispose failed:", e); }
      apiRef.current = null;
    };
    // Intentionally only re-init when the appointment changes (or a manual rejoin) — onClose is
    // kept in a ref so parent re-renders (dashboard polling) never tear down a live call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, reloadKey]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col" data-testid="jitsi-room">
      <div className="flex items-center justify-between px-4 py-2 text-white">
        <span className="font-heading font-bold flex items-center gap-2"><Video className="w-5 h-5 text-alert" /> Virtual Consultation</span>
        <div className="flex items-center gap-2">
          {!loading && !err && mode !== "text" && (
            <button onClick={toggleLowData} data-testid="low-data-toggle" className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-3 py-1.5 transition-colors ${lowData ? "bg-alert text-ink" : "bg-white/15 hover:bg-white/25 text-white"}`}>
              <Gauge className="w-4 h-4" /> {lowData ? "Low-data: ON" : "Low-data mode"}
            </button>
          )}
          <button onClick={onClose} data-testid="jitsi-close" className="bg-white/15 hover:bg-white/25 rounded-full p-2"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="flex-1 relative">
        {loading && !err && <div className="absolute inset-0 flex items-center justify-center text-white gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Connecting…</div>}
        {err && <div className="absolute inset-0 flex items-center justify-center text-white px-6 text-center">{err}</div>}
        {lowData && !err && <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-alert text-ink text-xs font-semibold rounded-full px-3 py-1" data-testid="low-data-badge">Low-data mode — audio prioritised</div>}
        {connLost && !err && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-5 bg-black/85 px-6 text-center text-white" data-testid="jitsi-reconnect">
            <div className="rounded-full bg-white/10 p-4"><WifiOff className="w-9 h-9 text-alert" /></div>
            <div className="space-y-1">
              <p className="font-heading font-bold text-lg">Connection lost</p>
              <p className="text-sm text-white/70 max-w-xs">
                {online
                  ? "You're back online — reconnecting you automatically…"
                  : "We can't reach the network right now. We'll reconnect you the moment you're back online."}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={rejoin}
                disabled={!online}
                data-testid="jitsi-rejoin-btn"
                className="inline-flex items-center gap-2 rounded-full bg-alert px-5 py-2.5 font-heading font-bold text-ink transition-colors hover:bg-alert/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${online ? "animate-spin" : ""}`} /> {online ? "Rejoin now" : "Waiting for network…"}
              </button>
              <button
                onClick={onClose}
                data-testid="jitsi-reconnect-leave"
                className="inline-flex items-center gap-2 rounded-full bg-white/15 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-white/25"
              >
                Leave
              </button>
            </div>
          </div>
        )}
        <div ref={ref} className="w-full h-full" />
      </div>
    </div>
  );
}
